import { useEffect, useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { format, parseISO } from "date-fns";
import { createFileRoute, Link } from "@tanstack/react-router";
import { GrowHeader, GrowFooter } from "@/components/site/GrowHeader";
import boardroomAssetSrc from "@/assets/boardroom.png";
const boardroomAsset = { url: boardroomAssetSrc };
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LicenseCheckout } from "@/components/LicenseCheckout";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { useSession } from "@/hooks/useSession";
import { getStripeEnvironment } from "@/lib/stripe";
import { getCheckoutSummary } from "@/utils/payments.functions";
import { COACHING, PLANS, toPlanId, type PlanId } from "@/lib/products";
import { SITE_URL } from "@/lib/seo";
import { useCertifications } from "@/hooks/useCertifications";
import { useScheduledSessions } from "@/hooks/useScheduledSessions";
import { getCourse } from "@/content/courses";
import { SessionTimeRange } from "@/components/site/UpcomingSessions";

const TITLE = "Start a Leadership Course | Leadership Edge Live";
const DESCRIPTION =
  "Buy a leadership course for yourself. One course for $49, a certification for $129, or every course for $349 a year.";

export const Route = createFileRoute("/start")({
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    plan?: string;
    coaching?: string;
    session_id?: string;
    cert?: string;
    session?: string;
  } => ({
    plan: typeof search.plan === "string" ? search.plan : undefined,
    // Search values arrive JSON-parsed, so coaching=1 becomes the number 1.
    coaching:
      search.coaching === undefined || search.coaching === null
        ? undefined
        : String(search.coaching),
    session_id: typeof search.session_id === "string" ? search.session_id : undefined,
    // Training Pipeline certification id, preselected from /grow.
    cert: search.cert === undefined || search.cert === null ? undefined : String(search.cert),
    // Scheduled session id, preselected from /schedule.
    session:
      search.session === undefined || search.session === null ? undefined : String(search.session),
  }),
  head: () => ({
    links: [{ rel: "canonical", href: `${SITE_URL}/start/` }],
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
  }),
  component: StartPage,
});

const primaryBtn =
  "inline-flex items-center justify-center rounded-[6px] bg-[#693D94] px-6 py-3 text-sm font-semibold text-primary-foreground shadow-brand transition-transform hover:-translate-y-0.5";
const linkBtn = "text-sm font-semibold text-primary underline";

function StartPage() {
  const {
    plan: planParam,
    coaching: coachingParam,
    session_id: sessionId,
    cert: certParam,
    session: scheduledSessionParam,
  } = Route.useSearch();

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <PaymentTestModeBanner />
      <GrowHeader />
      <main className="flex-1">
        {sessionId ? (
          <Confirmation planId={toPlanId(planParam)} sessionId={sessionId} />
        ) : (
          <StartForm
            initialPlan={toPlanId(planParam)}
            initialCoaching={
              coachingParam === "yes" || coachingParam === "1" || coachingParam === "true"
            }
            initialCertificationId={certParam ?? ""}
            initialSessionId={scheduledSessionParam ?? ""}
          />
        )}
      </main>
      <GrowFooter />
    </div>
  );
}

function Confirmation({ planId, sessionId }: { planId: PlanId; sessionId: string }) {
  const plan = PLANS.find((p) => p.id === planId) ?? PLANS[1]!;
  const oneTime = plan.mode === "payment";
  const [state, setState] = useState<"loading" | "paid" | "unverified">("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await getCheckoutSummary({
          data: { sessionId, environment: getStripeEnvironment() },
        });
        if (cancelled) return;
        if ("error" in result) {
          setState("unverified");
          return;
        }
        setState(result.summary.paymentStatus === "paid" ? "paid" : "unverified");
      } catch {
        if (!cancelled) setState("unverified");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  if (state === "loading") {
    return (
      <section className="mx-auto max-w-2xl px-5 py-16 sm:px-6 md:py-24">
        <p className="text-base text-muted-foreground">Confirming your payment...</p>
      </section>
    );
  }

  if (state === "unverified") {
    return (
      <section className="mx-auto max-w-2xl px-5 py-16 sm:px-6 md:py-24">
        <p className="text-xs font-semibold uppercase tracking-wider text-primary">
          Payment not confirmed
        </p>
        <h1 className="mt-4 font-display text-[2rem] leading-tight tracking-tight text-ink md:text-4xl">
          We couldn't confirm that payment.
        </h1>
        <p className="mt-5 text-base leading-relaxed text-muted-foreground">
          If you were charged, tell us and we'll sort it out. If not, you can try again.
        </p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Link to="/contact/" className={primaryBtn}>
            Contact us
          </Link>
          <Link
            to="/start/"
            className="inline-flex items-center justify-center rounded-[6px] border border-primary/30 px-6 py-3 text-sm font-semibold text-primary transition-colors hover:bg-lavender"
          >
            Start over
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-2xl px-5 py-16 sm:px-6 md:py-24">
      <p className="text-xs font-semibold uppercase tracking-wider text-primary">
        Payment complete
      </p>
      <h1 className="mt-4 font-display text-[2rem] leading-tight tracking-tight text-ink md:text-4xl">
        Thanks. Here's what happens next.
      </h1>
      {oneTime ? (
        <ul className="mt-8 space-y-5">
          {[
            "You'll get an email to set up your account.",
            "A Development Pro will get you enrolled in your first course.",
            plan.purchaseType === "certification"
              ? "Your purchase does not expire. Take your courses whenever you're ready."
              : "Your purchase does not expire. Take your course whenever you're ready.",
          ].map((line) => (
            <li
              key={line}
              className="rounded-2xl border border-border/60 bg-background p-5 text-base leading-relaxed text-muted-foreground"
            >
              {line}
            </li>
          ))}
        </ul>
      ) : (
        <ul className="mt-8 space-y-5">
          {[
            "You'll get an email to set up your account.",
            "A Development Pro will get you enrolled in your first course and check in as you go.",
            "Your plan runs for a year and renews unless you cancel.",
          ].map((line) => (
            <li
              key={line}
              className="rounded-2xl border border-border/60 bg-background p-5 text-base leading-relaxed text-muted-foreground"
            >
              {line}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-9 flex flex-col gap-3 sm:flex-row">
        <Link to="/schedule/" className={primaryBtn}>
          See upcoming dates
        </Link>
        <Link
          to="/course-catalog/"
          className="inline-flex items-center justify-center rounded-[6px] border border-primary/30 px-6 py-3 text-sm font-semibold text-primary transition-colors hover:bg-lavender"
        >
          Browse courses
        </Link>
      </div>
      <p className="mt-8 text-sm leading-relaxed text-muted-foreground">
        Nothing in your inbox within a business day?{" "}
        <Link to="/contact/" className="font-semibold text-primary underline">
          Tell us
        </Link>{" "}
        and we'll sort it out.
      </p>
    </section>
  );
}

function StartForm({
  initialPlan,
  initialCoaching,
  initialCertificationId,
  initialSessionId,
}: {
  initialPlan: PlanId;
  initialCoaching: boolean;
  initialCertificationId: string;
  initialSessionId: string;
}) {
  const [planId, setPlanId] = useState<PlanId>(initialPlan);
  const [coachingOn, setCoachingOn] = useState(initialCoaching);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const fullName = [firstName.trim(), lastName.trim()].filter(Boolean).join(" ");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [certification, setCertification] = useState(initialCertificationId);
  const [sessionId, setSessionId] = useState(initialSessionId);
  const [returnUrl, setReturnUrl] = useState("");
  const { user } = useSession();
  const { certifications, loading: certsLoading, error: certsError } = useCertifications();

  const plan = PLANS.find((p) => p.id === planId) ?? PLANS[1]!;
  const coachingAvailable = plan.id === "unlimited";
  const withCoaching = coachingAvailable && coachingOn;
  const certificationRequired = plan.id === "certification";
  const chosenCertification = certificationRequired
    ? certifications.find((c) => String(c.id) === certification)
    : undefined;
  const courseRequired = plan.id === "one-course";
  // Upcoming sessions, plus the one linked from /schedule even if its start
  // date has passed — otherwise that Register link lands on an empty picker.
  const { all: allSessions, upcoming: upcomingList } = useScheduledSessions();
  const sessionOptions = useMemo(() => {
    const list = upcomingList;
    if (initialSessionId && !list.some((s) => s.id === initialSessionId)) {
      const linked = allSessions.find((s) => s.id === initialSessionId);
      if (linked) return [linked, ...list];
    }
    return list;
  }, [initialSessionId, allSessions, upcomingList]);
  const chosenSession = courseRequired ? sessionOptions.find((s) => s.id === sessionId) : undefined;
  const chosenSessionLabel = chosenSession
    ? `${getCourse(chosenSession.courseSlug)?.title ?? chosenSession.courseSlug} — ${format(
        parseISO(chosenSession.startDate),
        "MMM d, yyyy",
      )}`
    : undefined;

  if (returnUrl) {
    return (
      <section className="mx-auto max-w-2xl px-5 py-12 sm:px-6 md:py-16">
        {/* Let people leave the card step without using the browser back button. */}
        <button
          type="button"
          onClick={() => setReturnUrl("")}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to your details
        </button>
        <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-primary">Checkout</p>
        <h1 className="mt-4 font-display text-[1.9rem] leading-tight tracking-tight text-ink md:text-4xl">
          Almost done. Enter your card details.
        </h1>
        <p className="mt-4 text-base text-muted-foreground">
          {plan.name}, {plan.price} {plan.priceNote}
          {withCoaching ? `, plus coaching ${COACHING.price} ${COACHING.priceNote}` : ""}.
          {chosenCertification ? ` ${chosenCertification.name}.` : ""}
          {chosenSessionLabel ? ` ${chosenSessionLabel}.` : ""}
        </p>
        <div className="mt-8 rounded-2xl border border-border/60 bg-background p-4 sm:p-7">
          <LicenseCheckout
            items={[
              { priceId: plan.priceLookupKey, quantity: 1 },
              ...(withCoaching ? [{ priceId: COACHING.lookupKey, quantity: 1 }] : []),
            ]}
            customerEmail={email || undefined}
            userId={user?.id}
            buyer={{
              fullName: fullName || undefined,
              firstName: firstName.trim() || undefined,
              lastName: lastName.trim() || undefined,
              phone: phone || undefined,
              ...(chosenCertification
                ? {
                    certification: chosenCertification.name,
                    certificationId: String(chosenCertification.id),
                  }
                : {}),
              ...(chosenSessionLabel && chosenSession
                ? {
                    course: chosenSessionLabel,
                    sessionId: chosenSession.id,
                    sessionDate: chosenSession.startDate,
                    sessionTime: chosenSession.time,
                    ...(chosenSession.tpSessionIds?.length
                      ? { tpSessionIds: chosenSession.tpSessionIds }
                      : {}),
                    ...((chosenSession.courseId ?? getCourse(chosenSession.courseSlug)?.courseId)
                      ? {
                          courseId: String(
                            chosenSession.courseId ?? getCourse(chosenSession.courseSlug)?.courseId,
                          ),
                        }
                      : {}),
                  }
                : {}),
            }}
            returnUrl={returnUrl}
            {...(plan.mode ? { mode: plan.mode } : {})}
            purchaseType={plan.purchaseType}
          />
        </div>
        <button type="button" onClick={() => setReturnUrl("")} className={`mt-6 ${linkBtn}`}>
          Change your choice
        </button>
      </section>
    );
  }

  return (
    <>
      <section className="relative isolate overflow-hidden">
        <img
          src={boardroomAsset.url}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 -z-20 h-full w-full object-cover"
        />
        <div className="absolute inset-0 -z-10 bg-[#693D94]/[0.91]" aria-hidden="true" />
        <div className="mx-auto max-w-3xl px-5 py-14 sm:px-6 md:py-20">
          {/* Way back to where the individual journey starts. */}
          <Link
            to="/grow/"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/85 hover:text-white hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to plans and courses
          </Link>
          <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-white/80">
            Start where you are
          </p>
          <h1 className="mt-4 font-display text-[2.1rem] leading-[1.1] tracking-tight text-white sm:text-5xl">
            Pick what you need, then pay by card.
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-white/85">
            This is for you, not for anyone else. No company name, no forms to route through
            anybody.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setReturnUrl(
              `${window.location.origin}/start?plan=${plan.id}${
                withCoaching ? "&coaching=yes" : ""
              }&session_id={CHECKOUT_SESSION_ID}`,
            );
          }}
          className="mx-auto max-w-3xl px-5 sm:px-6"
        >
          <fieldset>
            <legend className="font-display text-2xl text-ink">What you're buying</legend>
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {PLANS.map((p) => {
                const selected = p.id === planId;
                return (
                  <label
                    key={p.id}
                    className={`flex cursor-pointer flex-col rounded-2xl border bg-background p-5 transition-all sm:p-6 ${
                      selected
                        ? "border-primary shadow-brand"
                        : "border-border/60 hover:border-primary/30"
                    }`}
                  >
                    <span className="flex items-start justify-between gap-3">
                      <span className="font-display text-lg text-ink">{p.name}</span>
                      <input
                        type="radio"
                        name="plan"
                        value={p.id}
                        checked={selected}
                        onChange={() => setPlanId(p.id)}
                        className="mt-1 h-4 w-4 accent-[var(--primary)]"
                      />
                    </span>
                    {p.highlighted ? (
                      <span className="mt-2 text-xs font-semibold uppercase tracking-wider text-primary">
                        Most popular
                      </span>
                    ) : null}
                    <span className="mt-3 flex items-baseline gap-2">
                      <span className="font-display text-3xl text-ink">{p.price}</span>
                      <span className="text-sm text-muted-foreground">{p.priceNote}</span>
                    </span>
                    <span className="mt-3 text-sm leading-relaxed text-muted-foreground">
                      {p.body}
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          {courseRequired ? (
            <div className="mt-6 rounded-2xl border border-border/60 bg-background p-6 sm:p-7">
              <Label htmlFor="start-course" className="font-display text-lg text-ink">
                Which course?
              </Label>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Pick a scheduled session. You can change it later.
              </p>
              <select
                id="start-course"
                name="course"
                required
                value={sessionId}
                onChange={(e) => setSessionId(e.target.value)}
                className="mt-4 w-full rounded-[4px] border border-input bg-background px-3 py-2.5 text-sm text-ink"
              >
                <option value="">Choose a course</option>
                {sessionOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {getCourse(s.courseSlug)?.title ?? s.courseSlug} —{" "}
                    {format(parseISO(s.startDate), "MMM d, yyyy")}
                  </option>
                ))}
              </select>
              {chosenSession ? (
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                  <SessionTimeRange session={chosenSession} /> · {chosenSession.format}
                </p>
              ) : null}
            </div>
          ) : null}

          {certificationRequired ? (
            <div className="mt-6 rounded-2xl border border-border/60 bg-background p-6 sm:p-7">
              <Label htmlFor="start-certification" className="font-display text-lg text-ink">
                Which certification?
              </Label>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Each one is three courses that work together. You can change it later.
              </p>
              <select
                id="start-certification"
                name="certification"
                required
                disabled={certsLoading || certifications.length === 0}
                value={certification}
                onChange={(e) => setCertification(e.target.value)}
                className="mt-4 w-full rounded-[4px] border border-input bg-background px-3 py-2.5 text-sm text-ink disabled:opacity-60"
              >
                <option value="">
                  {certsLoading ? "Loading certifications..." : "Choose a certification"}
                </option>
                {certifications.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.name}
                  </option>
                ))}
              </select>
              {!certsLoading && (certsError || certifications.length === 0) ? (
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                  {certsError ??
                    "Certifications aren't available right now. Contact us and we'll get you set up."}
                </p>
              ) : null}
              {chosenCertification?.description ? (
                <div
                  className="mt-4 text-sm leading-relaxed text-muted-foreground [&_p]:mt-2 [&_p:first-child]:mt-0"
                  dangerouslySetInnerHTML={{ __html: chosenCertification.description }}
                />
              ) : null}
              {chosenCertification?.courses.length ? (
                <ol className="mt-4 space-y-2">
                  {chosenCertification.courses.map((course, i) => (
                    <li key={`${course.order}-${course.name}`} className="flex gap-3">
                      <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-lavender text-[11px] font-semibold text-[#693D94]">
                        {i + 1}
                      </span>
                      <span className="text-sm text-ink">{course.name}</span>
                    </li>
                  ))}
                </ol>
              ) : null}
            </div>
          ) : null}

          {coachingAvailable ? (
            <label className="mt-6 flex cursor-pointer items-start gap-4 rounded-2xl border border-dashed border-primary/30 bg-background/60 p-6 sm:p-7">
              <input
                type="checkbox"
                checked={coachingOn}
                onChange={(e) => setCoachingOn(e.target.checked)}
                className="mt-1 h-4 w-4 shrink-0 accent-[var(--primary)]"
              />
              <span className="flex-1">
                <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                  Add-on
                </span>
                <span className="mt-2 flex flex-wrap items-baseline justify-between gap-3">
                  <span className="font-display text-lg text-ink">{COACHING.name}</span>
                  <span className="flex items-baseline gap-2">
                    <span className="font-display text-2xl text-ink">{COACHING.price}</span>
                    <span className="text-sm text-muted-foreground">{COACHING.priceNote}</span>
                  </span>
                </span>
                <span className="mt-2 block text-sm leading-relaxed text-muted-foreground">
                  {COACHING.body}
                </span>
              </span>
            </label>
          ) : null}

          {withCoaching ? (
            <p className="mt-4 text-sm text-muted-foreground">
              Your total: {plan.price} for the plan plus {COACHING.price} for coaching, per year.
            </p>
          ) : null}

          <div className="mt-10 rounded-2xl border border-border/60 bg-background p-6 sm:p-7">
            <h2 className="font-display text-2xl text-ink">Where should we reach you?</h2>
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <div>
                <Label htmlFor="start-first-name" className="text-sm font-semibold text-ink">
                  First name
                </Label>
                <Input
                  id="start-first-name"
                  name="firstName"
                  required
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="start-last-name" className="text-sm font-semibold text-ink">
                  Last name
                </Label>
                <Input
                  id="start-last-name"
                  name="lastName"
                  required
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="start-email" className="text-sm font-semibold text-ink">
                  Email
                </Label>
                <Input
                  id="start-email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="start-phone" className="text-sm font-semibold text-ink">
                  Phone (optional)
                </Label>
                <Input
                  id="start-phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="mt-2"
                />
              </div>
            </div>

            <button type="submit" className={`mt-8 w-full sm:w-auto ${primaryBtn}`}>
              Continue to payment
            </button>
            <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
              You'll pay by card on the next step. Tax is calculated at checkout. Questions first?{" "}
              <Link to="/contact/" className="font-semibold text-primary underline">
                Talk to a Development Pro
              </Link>
              .
            </p>
          </div>
        </form>
      </section>
    </>
  );
}
